import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Subject, map, takeUntil } from 'rxjs';
import {
  AppLogger,
  Calender,
  ColumnDefinition,
  ColumnTemplateType,
  FormValidators,
  Icon,
  NestedMenu,
  TranformService,
} from 'app-core';
import { ClinicAppointmentService, DoctorService } from 'app-service';
import { AppointmentStatus, IAppointment, TimeSlot } from '@alpha/types';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';

import IconType = Data.Core.IconType;

interface AppointmentTable {
  UserName: string;
  Contact: string;
  Time: string;
  Status: string;
}

@Component({
  selector: 'app-clinic-appointments',
  templateUrl: './appointments.component.html',
  styleUrls: ['./appointments.component.scss'],
  viewProviders: [DoctorService, TranformService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicAppointmentsComponent implements OnInit, OnDestroy {
  @ViewChild('statusColumn', { static: true })
  public statusColumn?: TemplateRef<any>;
  @ViewChild('attendanceColumn', { static: true })
  public attendanceColumn?: TemplateRef<any>;
  @ViewChild('hiddenControl') public hiddenControl?: ElementRef;
  public appointmentData: any[] = [];
  public displayedColumns: Array<ColumnDefinition> = [];
  public calender: Calender = new Calender({
    placeholderText: 'Choose a date',
    hintText: '(Day/Month/Year)',
    opened: false,
    startAt: new Date(),
  });
  private selectedDate: Date = new Date();
  private selectedRowValue: any;
  public dateControl: FormControl = new FormControl([
    '',
    FormValidators.dateRangeValidator,
  ]);
  public dataSource = new MatTableDataSource(this.appointmentData);
  public sortingDataAccessor = (
    data: AppointmentTable,
    sortHeaderId: string,
  ): any => {
    switch (sortHeaderId) {
      case 'UserName':
      case 'Contact':
        return data[sortHeaderId].toLowerCase();
      case 'Time':
        return data[sortHeaderId];
      case 'Status':
        return data.Status;
      default:
        return null;
    }
  };
  public AppointmentStatus = AppointmentStatus;
  public cancelAppointmentOptions: NestedMenu = new NestedMenu({
    icon: new Icon({
      icon: IconType.moreVertIcon,
    }),
    items: [
      {
        parent: {
          icon: new Icon({
            icon: IconType.closeIcon,
          }),
          label: 'Cancel Appointment',
          onClick: () => {
            this.changeAppointmentStatus(
              this.selectedRowValue._id,
              AppointmentStatus.Rejected,
            );
          },
        },
      },
    ],
  });
  public attendAppointmentOptions: NestedMenu = new NestedMenu({
    icon: new Icon({
      icon: IconType.moreVertIcon,
    }),
    items: [
      {
        parent: {
          icon: new Icon({
            icon: IconType.checkIcon,
          }),
          label: 'Attend Appointment',
          onClick: () => {
            this.changeAppointmentStatus(
              this.selectedRowValue._id,
              AppointmentStatus.Completed,
            );
          },
        },
      },
      {
        parent: {
          icon: new Icon({
            icon: IconType.editModeIcon,
          }),
          label: 'Prescribe Medicine',
          onClick: () => {
            this.changeAppointmentStatus(
              this.selectedRowValue._id,
              AppointmentStatus.Completed,
            );
          },
        },
      },
      {
        parent: {
          icon: new Icon({
            icon: IconType.closeIcon,
          }),
          label: 'Cancel Appointment',
          onClick: () => {
            this.changeAppointmentStatus(
              this.selectedRowValue._id,
              AppointmentStatus.Rejected,
            );
          },
        },
      },
      {
        parent: {
          icon: new Icon({
            icon: IconType.closeIcon,
          }),
          label: 'Patient is not available',
          onClick: () => {
            this.changeAppointmentStatus(
              this.selectedRowValue._id,
              AppointmentStatus.Unavailable,
            );
          },
        },
      },
    ],
  });
  public prescribeMedicineOptions: NestedMenu = new NestedMenu({
    icon: new Icon({
      icon: IconType.moreVertIcon,
    }),
    items: [
      {
        parent: {
          icon: new Icon({
            icon: IconType.editModeIcon,
          }),
          label: 'Prescribe Medicine',
          onClick: () => {
            const route = `/clinic/appointments/prescription/edit/${this.selectedRowValue._id}`;
            this._route
              .navigate([route], { queryParamsHandling: 'merge' })
              .catch((err: string | Error) => {
                debugger;
                AppLogger.Error(err);
              });
          },
        },
      },
    ],
  });
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private clinicId: string = '';

  public constructor(
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _appointmentService: ClinicAppointmentService,
    private readonly _route: Router,
  ) {}

  public ngOnInit(): void {
    this.getAppointments();
    this.getDisplayedColumns();
    this.dateControl.setValue(new Date());
  }

  public getDisplayedColumns(): void {
    this.displayedColumns = [
      {
        id: 'id',
        displayName: '#',
        templateType: ColumnTemplateType.Text,
        requestedWidth: 10,
      },
      {
        id: 'UserName',
        displayName: 'User Name',
        templateType: ColumnTemplateType.Text,
        requestedWidth: 25,
      },
      {
        id: 'Contact',
        displayName: 'Contact',
        templateType: ColumnTemplateType.Text,
        requestedWidth: 15,
      },
      {
        id: 'Time',
        displayName: 'Time',
        templateType: ColumnTemplateType.Text,
        requestedWidth: 12,
      },
      {
        id: 'Status',
        displayName: 'Status',
        templateType: ColumnTemplateType.Custom,
        template: this.statusColumn,
        requestedWidth: 15,
      },
      {
        id: '_id',
        displayName: 'Mark Attendance',
        templateType: ColumnTemplateType.Custom,
        template: this.attendanceColumn,
      },
    ];
  }

  public getAppointments(): void {
    this.clinicId = this._route.url.split('/')[3].split('?')[0];
    this._appointmentService
      .findAllAppointmentsByDoctorsId({
        date: this.selectedDate || new Date(),
        clinicId: {
          _id: this.clinicId.toString(),
        },
      })
      .pipe(
        map((appointments: Array<IAppointment>) => {
          let responseObj: any[] = [];
          if (appointments.length > 0) {
            appointments.sort((a: any, b: any) => {
              // Sort by time (ascending)
              if (a.time !== b.time) {
                return a.time - b.time;
              }

              // Sort by createdAt (ascending), handling undefined
              const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;

              return aDate - bDate;
            });
            appointments.forEach((appointment: IAppointment, index: number) => {
              responseObj.push({
                id: index + 1,
                _id: appointment._id,
                UserName: `${appointment.userId.firstName} ${appointment.userId.lastName}`,
                Contact: `${appointment.userId.mobile?.id}`,
                Time: `${TimeSlot[appointment.time]}`,
                Status: `${AppointmentStatus[appointment.status || 0]}`,
                disabled:
                  index === 0 &&
                  appointments[index]!.status === AppointmentStatus.Pending
                    ? false
                    : appointments[index - 1]! &&
                        appointments[index - 1]!.status ===
                          AppointmentStatus.Pending
                      ? true
                      : false,
              });
            });
            return responseObj;
          }
          return responseObj;
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((response: any) => {
        this.appointmentData = response;
        this.dataSource = new MatTableDataSource(this.appointmentData);
        this._cdRef.markForCheck();
      });
  }

  public changeAppointmentStatus(
    item: string,
    status: AppointmentStatus = AppointmentStatus.Completed,
  ): void {
    this._appointmentService
      .updateAppointment(item, {
        status: status,
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const index = this.appointmentData.findIndex((id) => id._id === item);
        if (index !== -1) {
          this.appointmentData[index].Status = AppointmentStatus[status];
          if (this.appointmentData[index + 1]) {
            this.appointmentData[index + 1].disabled = false;
          }
          this.dataSource = new MatTableDataSource(this.appointmentData);
          this._cdRef.markForCheck();
        } else {
          AppLogger.Error(`Appointment Status for id ${item} not found`);
        }
      });
  }

  public checkAppointmentStatus(item: any): boolean {
    return (
      item.Status !== AppointmentStatus[AppointmentStatus.Completed] &&
      this._appointmentService.isTodayOrGreater(this.selectedDate)
    );
  }

  public isAppointmentPending(item: any): boolean {
    return (
      item.Status === AppointmentStatus[AppointmentStatus.Pending] &&
      this._appointmentService.isTodayOrGreater(this.selectedDate)
    );
  }

  public selectedRow(item: any): void {
    this.selectedRowValue = item;
  }

  public isAppointmentCompleted(item: any): boolean {
    return item.Status === AppointmentStatus[AppointmentStatus.Completed];
  }

  public onDateChanege(selectedDate: Date | null): void {
    const today = new Date();
    if (selectedDate === null) {
      selectedDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
    }
    this.selectedDate = selectedDate;
    this.getAppointments();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
